import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from '../i18n';
import { useNbTheme } from '../hooks/useNbTheme';
import BadgeNB from './ui-nb/BadgeNB';
import {
  industryExamples,
  setCurrentIndustryIndex,
  localize,
  type IndustryExample,
} from '../data/landing-examples';

const ROTATION_MS = 25000;
const TICK_MS = 100;

function statusVariant(
  status: string
): 'success' | 'warning' | 'error' | 'info' {
  switch (status) {
    case 'diagnosing':
      return 'warning';
    case 'waitingParts':
      return 'error';
    case 'inProgress':
    default:
      return 'info';
  }
}

export default function DashboardPreviewNB() {
  const { t, language } = useTranslation();
  const { colors } = useNbTheme();

  const [activeIndex, setActiveIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const sectionRef = useRef<HTMLElement>(null);

  const example: IndustryExample = industryExamples[activeIndex];
  const d = example.dashboard;

  // ── IntersectionObserver: pause when section scrolls out of view ──

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.2 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // ── Progress bar ticker ──

  useEffect(() => {
    if (!isVisible) return;

    const increment = (TICK_MS / ROTATION_MS) * 100;

    const timer = setInterval(() => {
      setProgress((prev) => {
        const next = prev + increment;
        if (next >= 100) {
          const nextIdx = (activeIndex + 1) % industryExamples.length;
          // Schedule index change via setTimeout to avoid setState-inside-setState
          setTimeout(() => {
            setActiveIndex(nextIdx);
            setCurrentIndustryIndex(nextIdx);
          }, 0);
          return 0;
        }
        return next;
      });
    }, TICK_MS);

    return () => clearInterval(timer);
  }, [isVisible, activeIndex]);

  // Reset progress on manual index change
  const jumpTo = useCallback((index: number) => {
    setActiveIndex(index);
    setProgress(0);
    setCurrentIndustryIndex(index);
  }, []);

  // ── Scroll to TrackDemo section ──

  const scrollToDemo = useCallback(() => {
    document
      .getElementById('track-demo')
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  // ── Styles ──

  const cardStyle = {
    background: colors.bgCard,
    border: `3px solid ${colors.textPrimary}`,
    borderRadius: '2px',
    padding: 'clamp(1rem, 2vw, 1.5rem)',
    position: 'relative' as const,
    overflow: 'hidden',
  };

  const barColor = colors.accentPrimary || '#FFD600';

  return (
    <section
      ref={sectionRef}
      className="py-12 md:py-20"
      style={{ background: colors.bgPage }}
    >
      <div className="max-w-[1200px] mx-auto px-4 md:px-8">
        {/* Section header */}
        <div className="mb-8 md:mb-12">
          <h2
            className="font-nb font-bold mb-3"
            style={{
              color: colors.textPrimary,
              fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
              lineHeight: 1.2,
            }}
          >
            {t('dashboard.title')}
          </h2>
          <p
            className="font-nb max-w-xl"
            style={{
              color: colors.textSecondary,
              fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
              lineHeight: 1.6,
            }}
          >
            {t('dashboardPreview.subtitle')}
          </p>
        </div>

        {/* Dashboard card */}
        <div style={cardStyle}>
          {/* ── Cooldown bar ── */}
          <div
            className="absolute top-0 left-0"
            style={{
              height: '4px',
              width: `${progress}%`,
              background: barColor,
              transition: `width ${TICK_MS}ms linear`,
              zIndex: 1,
            }}
          />

          {/* ── Industry badge ── */}
          <div
            className="flex items-center justify-between mb-4 pt-1"
            style={{ position: 'relative', zIndex: 2 }}
          >
            <div className="flex items-center gap-3">
              <h3
                className="font-nb font-bold text-lg"
                style={{ color: colors.textPrimary }}
              >
                {localize(d.title, language)}
              </h3>
              <BadgeNB variant="warning">
                {t('dashboardPreview.live')}
              </BadgeNB>
            </div>
          </div>

          {/* ── Bento Grid ── */}
          <div
            className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6"
            style={{ position: 'relative', zIndex: 2 }}
          >
            {/* Row 1-2, Col 1-2: Active Repairs */}
            <div className="md:col-span-2 md:row-span-2">
              <div className="space-y-3">
                {d.repairs.map((repair, i) => (
                  <div
                    key={repair.id}
                    className="flex items-center gap-3 py-2"
                    style={{
                      borderBottom:
                        i < d.repairs.length - 1
                          ? `2px solid ${colors.textPrimary}`
                          : 'none',
                      opacity: i < d.repairs.length - 1 ? 1 : 0.1,
                    }}
                  >
                    <span
                      className="font-nb-mono text-xs font-bold min-w-[80px]"
                      style={{ color: colors.textSecondary }}
                    >
                      {repair.id}
                    </span>
                    <span
                      className="font-nb text-sm flex-1"
                      style={{ color: colors.textPrimary }}
                    >
                      {localize(repair.device, language)}
                    </span>
                    <BadgeNB variant={statusVariant(repair.status)}>
                      {t(`dashboardPreview.status.${repair.status}`)}
                    </BadgeNB>
                  </div>
                ))}
              </div>
              <div
                className="mt-4 pt-3"
                style={{ borderTop: `2px solid ${colors.textPrimary}` }}
              >
                <span
                  className="font-nb font-bold text-sm"
                  style={{ color: colors.accentFunctional || '#0D9488' }}
                >
                  {t('dashboardPreview.viewAll')} &rarr;
                </span>
              </div>
            </div>

            {/* Row 1, Col 3: Today's metrics */}
            <div>
              <h4
                className="font-nb font-bold text-base mb-3"
                style={{ color: colors.textPrimary }}
              >
                {t('dashboardPreview.today')}
              </h4>
              <div className="space-y-2">
                {[
                  { value: d.metrics.received, label: 'received', color: colors.textPrimary },
                  { value: d.metrics.delivered, label: 'delivered', color: colors.accentFunctional || '#0D9488' },
                  { value: d.metrics.pending, label: 'pending', color: colors.accentPrimary || '#FFD600' },
                ].map((m) => (
                  <div key={m.label}>
                    <span
                      className="font-nb-mono text-xl font-bold"
                      style={{ color: m.color }}
                    >
                      {m.value}
                    </span>
                    <p
                      className="font-nb text-sm"
                      style={{ color: colors.textSecondary }}
                    >
                      {t(`dashboardPreview.${m.label}`)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Row 2, Col 3: Technicians */}
            <div>
              <h4
                className="font-nb font-bold text-base mb-3"
                style={{ color: colors.textPrimary }}
              >
                {t('dashboardPreview.technicians')}
              </h4>
              <div className="flex items-end gap-2 mb-2">
                <span
                  className="font-nb-mono text-2xl font-bold leading-none"
                  style={{ color: colors.textPrimary }}
                >
                  {d.technicians.active}
                </span>
                <span
                  className="font-nb text-sm"
                  style={{ color: colors.accentFunctional || '#0D9488' }}
                >
                  {t('dashboardPreview.active')}
                </span>
              </div>
              <div className="flex gap-1" style={{ height: '24px' }}>
                {Array.from({ length: d.technicians.total }, (_, i) => (
                  <div
                    key={i}
                    className="flex-1"
                    style={{
                      background:
                        i < d.technicians.active
                          ? colors.accentFunctional || '#0D9488'
                          : colors.bgSection || '#F0F0F0',
                      border: `2px solid ${colors.textPrimary}`,
                    }}
                  />
                ))}
              </div>
              <p className="font-nb text-xs mt-2" style={{ color: colors.textSecondary }}>
                {d.technicians.active} of {d.technicians.total} {t('dashboardPreview.active')}
              </p>
            </div>

            {/* Row 3, Col 1: Alerts */}
            <div>
              <h4
                className="font-nb font-bold text-base mb-3"
                style={{ color: colors.textPrimary }}
              >
                {t('dashboardPreview.alerts')}
              </h4>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-nb text-sm" style={{ color: colors.textSecondary }}>
                    {t('dashboardPreview.pendingParts')}
                  </span>
                  <BadgeNB variant={d.alerts.pendingParts > 0 ? 'error' : 'info'}>
                    {d.alerts.pendingParts}
                  </BadgeNB>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-nb text-sm" style={{ color: colors.textSecondary }}>
                    {t('dashboardPreview.delayedDeliveries')}
                  </span>
                  <BadgeNB variant={d.alerts.delayed > 0 ? 'warning' : 'info'}>
                    {d.alerts.delayed}
                  </BadgeNB>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-nb text-sm" style={{ color: colors.textSecondary }}>
                    {t('dashboardPreview.overdue')}
                  </span>
                  <BadgeNB variant={d.alerts.overdue > 0 ? 'error' : 'info'}>
                    {d.alerts.overdue}
                  </BadgeNB>
                </div>
              </div>
            </div>

            {/* Row 3, Col 2-3: Recent Activity */}
            <div className="md:col-span-2">
              <h4
                className="font-nb font-bold text-base mb-3"
                style={{ color: colors.textPrimary }}
              >
                {t('dashboardPreview.recentActivity')}
              </h4>
              <div className="space-y-2">
                {d.activity.map((a, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span
                      className="font-nb-mono text-xs min-w-[44px] pt-0.5"
                      style={{ color: colors.textMuted }}
                    >
                      {a.time}
                    </span>
                    <div>
                      <span
                        className="font-nb text-sm font-medium"
                        style={{ color: colors.textPrimary }}
                      >
                        {t(`dashboardPreview.activity.${a.actionKey}`)}
                      </span>
                      <span
                        className="font-nb text-xs ml-2"
                        style={{ color: colors.textSecondary }}
                      >
                        {localize(a.detail, language)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── "Try demo" floating button ── */}
          <div className="flex justify-end mt-5" style={{ position: 'relative', zIndex: 2 }}>
            <button
              onClick={scrollToDemo}
              className="font-nb font-bold text-sm flex items-center gap-2 px-4 py-2 border-3 transition-all duration-200 hover:-translate-y-0.5"
              style={{
                background: colors.accentPrimary || '#FFD600',
                color: '#1C1C1C',
                borderColor: colors.textPrimary,
                borderRadius: '2px',
              }}
            >
              {t('dashboardPreview.tryDemo')} &rarr;
            </button>
          </div>
        </div>

        {/* ── Industry dots (carousel indicators) ── */}
        <div className="flex justify-center gap-2 mt-5">
          {industryExamples.map((ind, i) => (
            <button
              key={ind.id}
              onClick={() => jumpTo(i)}
              className="border-3 transition-all duration-200"
              style={{
                width: i === activeIndex ? '28px' : '12px',
                height: '12px',
                borderRadius: i === activeIndex ? '6px' : '50%',
                background:
                  i === activeIndex
                    ? colors.accentPrimary || '#FFD600'
                    : 'transparent',
                borderColor: colors.textPrimary,
              }}
              aria-label={localize(ind.name, language)}
            />
          ))}
        </div>

        {/* Trust note */}
        <p
          className="font-nb text-xs mt-4 text-center"
          style={{ color: colors.textMuted }}
        >
          {t('dashboardPreview.mockNote')}
        </p>
      </div>
    </section>
  );
}
